import React, { useEffect, useMemo, useState } from "react";
import { buttonStyles } from "styles/button";
import Spinner from "theme/Spinner";
import Text from "theme/Text";
import { View, Button as RNButton, StyleSheet, Pressable } from "react-native";
import { textProps } from "styles/text";
import LinearGradientBackground from 'components/atoms/LinearGradientBackground'
import _ from 'lodash'
import HelperString from 'utils/helper-string'
import isEqual from 'react-fast-compare'
import { colorList } from 'utils/colors'
import { ThemedValues } from 'styles/themed'

const Button = ({ onLayout, disabled = false, isLoading = false, isLoadingText, icon, onPress, size = 'medium', status = 'primary', appearance = 'filled', color = undefined, style, textStyle, children, accessibilityLabel, accessoryLeft, accessoryRight }) => {
	
	// Set the base text
	let text = children;
	
	// Create the props
	const buttonProps = {};
	
	if( accessoryLeft ) {
		buttonProps['accessoryLeft'] = accessoryLeft;
	}

	if( accessoryRight ) {
		buttonProps['accessoryRight'] = accessoryRight;
	}

	if( icon ) {
		buttonProps['accessoryLeft'] = icon;
	}
	
	// Set the button loading
	if( isLoading === true ) {
		buttonProps['accessoryLeft'] = (
			<ButtonLoadingIndicator
				appearance={ appearance } 
			/>
		);

		if( isLoadingText ) {
			text = isLoadingText;
		}
	}

	const sizeContainerStyle = useMemo(() => {
		switch( size ) {
			case 'small':
				return buttonStyles.buttonSmall;

			case 'large':
				return buttonStyles.buttonLarge;

			case 'medium':
			default:
				return buttonStyles.buttonDefault;
		}
	}, [ size ] )

	const sizeTextStyle = useMemo(() => {
		switch( size ) {
			case 'small':
				return buttonStyles.buttonSmallText;

			case 'large':
				return buttonStyles.buttonLargeText;

			case 'medium':
			default:
				return buttonStyles.buttonDefaultText;
		}
	}, [ size ] )

	const appearanceContainerStyle = useMemo(() => {
		switch( appearance ) {
			case 'inverse':
				return buttonStyles.buttonInverse;

			case 'outline':
				return buttonStyles.buttonOutline;

			case 'ghost':
				return buttonStyles.buttonGhost;

			default:
				return buttonStyles.button;
		}
	}, [ appearance ] )

	const appearanceTextStyle = useMemo(() => {

		if( textStyle ) {
			return textStyle;
		}

		switch( appearance ) {
			case 'inverse':
				return buttonStyles.buttonInverseText;

			case 'outline':
				return buttonStyles.buttonOutlineText;

			case 'ghost':
				return buttonStyles.buttonGhostText;

			default:
				return buttonStyles.buttonText;
		}
	}, [ appearance, color, textStyle ] )

	const textContent = [];
	if( text && text !== '' ) {
		if( appearance === 'filled' ) {
			textContent.push(
				<LinearGradientBackground
					key={ HelperString.hash('themed-button-linear-gradient', text ) }
					maxOpacity={ 0.2 }
				/>
			)
		}

		textContent.push(
			<ButtonText
				key={ HelperString.hash('themed-button-text', text ) }
				style={[ appearanceTextStyle, sizeTextStyle ]}>
				
				{ text }
			</ButtonText>
		);
	}

	if( textContent.length > 0 ) {
		// Check if has accessory and add if set (for centering)
		if( _.has( buttonProps, 'accessoryLeft' ) && !_.has( buttonProps, 'accessoryRight' ) ) {
			buttonProps['accessoryRight'] = ( props ) => {
				return <View {...props} />;
			}
		}
		
		if( _.has( buttonProps, 'accessoryRight' ) && !_.has( buttonProps, 'accessoryLeft' ) ) {
			buttonProps['accessoryLeft'] = ( props ) => {
				return <View {...props} />;
			}
		}
	}
	
	return (
		<Pressable
			onLayout={ onLayout }
			style={({ pressed }) => {
				return (
					[
						styles.container,
						buttonStyles.button,
						appearanceContainerStyle,
						sizeContainerStyle,
						style ? StyleSheet.flatten(style) : {},
						{
							opacity: (disabled || pressed) ? 0.5 : 1,
						},
						textContent.length === 0 ? { minWidth: 'auto' } : {}
					]
				);
			}}
			disabled={ disabled }
			onPress={ onPress }
			accessible
			accessibilityRole="button"
			accessibilityState={{ disabled, busy: isLoading }}
			accessibilityLabel={ accessibilityLabel || ( _.isString( text ) ? text : undefined ) }>
				
			<ButtonAccessory
				accessory={ buttonProps.accessoryLeft }
			/>

			{ textContent }

			<ButtonAccessory
				accessory={ buttonProps.accessoryRight }
				position="right"
			/>
		</Pressable>
	)
}

const ButtonAccessory = ({ accessory, position }) => {

	if( !accessory ) {
		return null;
	}

	return (
		<View 
			style={[ buttonStyles.iconContainer, position === "right" ? buttonStyles.iconContainerRight : buttonStyles.iconContainerLeft ]}>
				
			{ _.isFunction( accessory ) ? accessory() : accessory }
		</View>
	)
}

const ButtonText = ({ style, children }) => {

	return (
		<Text
			{ ...textProps.fit }
			style={[
				buttonStyles.buttonText,
				style
			]}>
			
			{ children }
		</Text>
	)
}

const ButtonLoadingIndicator = ( props ) => {
	const viewStyle = [
		props.style,
		buttonStyles.indicator
	]
	
	return (
		<View
			style={ viewStyle }>
			
			<Spinner
				color={ props.color || colorList.WHITE.primary }
				size={ 'small' }
			/>
		</View>
	)
};

const styles = StyleSheet.create({
	container: {
		flexDirection: "row",
		alignItems: "center",
		borderRadius: ThemedValues.borderRadius,
	},
})

export default React.memo( Button, isEqual );
